import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  InputBase,
  Grid,
  Card,
  CardActionArea,
  Chip,
  Tabs,
  Tab,
  Tooltip,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// Icons
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import AppsIcon from '@mui/icons-material/Apps';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import LocalDiningIcon from '@mui/icons-material/LocalDining';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InventoryIcon from '@mui/icons-material/Inventory';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BadgeIcon from '@mui/icons-material/Badge';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PeopleIcon from '@mui/icons-material/People';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StorefrontIcon from '@mui/icons-material/Storefront';
import TvIcon from '@mui/icons-material/Tv';
import TabletMacIcon from '@mui/icons-material/TabletMac';
import ApartmentIcon from '@mui/icons-material/Apartment';
import TableBarIcon from '@mui/icons-material/TableBar';

// Comprehensive App Catalog (Clover & Square Parity)
export const APP_CATALOG = [
  // --- Floor Operations (FOH) ---
  {
    id: 'pos',
    name: 'Point of Sale (POS)',
    description: 'Fast register checkout, barcode scan & contactless payments',
    path: '/pos',
    category: 'foh',
    categoryLabel: 'Floor Operations',
    icon: <PointOfSaleIcon />,
    color: '#10B981', // Clover emerald green
    roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_CASHIER'],
    keywords: ['checkout', 'register', 'terminal', 'sell', 'orders', 'cart', 'cashier', 'clover', 'square']
  },
  {
    id: 'floor-plan',
    name: 'Floor Plan & Tables',
    description: 'Interactive dining room table map, occupancy & guest bill splitting',
    path: '/floor-plan',
    category: 'foh',
    categoryLabel: 'Floor Operations',
    icon: <TableBarIcon />,
    color: '#3B82F6', // Blue
    roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_CASHIER'],
    keywords: ['table', 'floor', 'dining', 'seats', 'guests', 'hostess', 'split', 'turns', 'layout']
  },
  {
    id: 'kitchen-display',
    name: 'Kitchen Display (KDS)',
    description: 'Live order queue, preparation timers & station dispatching',
    path: '/kitchen-display',
    category: 'foh',
    categoryLabel: 'Floor Operations',
    icon: <LocalDiningIcon />,
    color: '#EF4444', // Red ticket
    roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_CASHIER'],
    keywords: ['kds', 'chef', 'cook', 'kitchen', 'food', 'tickets', 'prep', 'orders']
  },
  {
    id: 'cash',
    name: 'Cash Drawer',
    description: 'Shift opening float, pay-ins, drops & end-of-day reconciliation',
    path: '/cash',
    category: 'foh',
    categoryLabel: 'Floor Operations',
    icon: <ReceiptIcon />,
    color: '#F59E0B', // Amber
    roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_CASHIER'],
    keywords: ['cash', 'drawer', 'float', 'money', 'reconciliation', 'till', 'drop']
  },
  {
    id: 'timeclock',
    name: 'Staff Time Clock',
    description: 'Employee punch clock, breaks, overtime & labor compliance',
    path: '/timeclock',
    category: 'foh',
    categoryLabel: 'Floor Operations',
    icon: <AccessTimeIcon />,
    color: '#6366F1', // Indigo
    roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_CASHIER'],
    keywords: ['clock', 'punch', 'hours', 'shifts', 'time', 'attendance', 'payroll']
  },

  // --- Supply Chain & Stock (BOH) ---
  {
    id: 'inventory',
    name: 'Inventory Master',
    description: 'Real-time stock tracking, multi-location counts & reorder alerts',
    path: '/inventory',
    category: 'boh',
    categoryLabel: 'Supply Chain',
    icon: <InventoryIcon />,
    color: '#3B82F6', // Blue
    roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'],
    keywords: ['stock', 'items', 'products', 'sku', 'catalog', 'quantity', 'reorder']
  },
  {
    id: 'supply-chain',
    name: 'Purchase Orders & Supply',
    description: 'Supplier PO creation, vendor catalogs & delivery tracking',
    path: '/supply-chain',
    category: 'boh',
    categoryLabel: 'Supply Chain',
    icon: <ReceiptIcon />,
    color: '#06B6D4', // Cyan
    roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'],
    keywords: ['po', 'purchase', 'vendors', 'suppliers', 'inbound', 'receiving']
  },
  {
    id: 'invoice-upload',
    name: 'Smart Invoice OCR',
    description: 'AI invoice scanning, line-item extraction & instant stock ingestion',
    path: '/invoice-upload',
    category: 'boh',
    categoryLabel: 'Supply Chain',
    icon: <AutoAwesomeIcon />,
    color: '#8B5CF6', // Purple AI
    roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'],
    keywords: ['ocr', 'scan', 'invoice', 'receipt', 'ai', 'pdf', 'bills']
  },
  {
    id: 'bom-builder',
    name: 'Recipe & BOM Builder',
    description: 'Bill of Materials, ingredient cost depletion & portion modeling',
    path: '/bom-builder',
    category: 'boh',
    categoryLabel: 'Supply Chain',
    icon: <InventoryIcon />,
    color: '#EC4899', // Pink
    roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'],
    keywords: ['recipe', 'bom', 'ingredients', 'portions', 'menu costing', 'food cost']
  },
  {
    id: 'variance-dashboard',
    name: 'Stock Variance & Loss',
    description: 'Discrepancy auditing, shrinkage tracking & waste analysis',
    path: '/variance-dashboard',
    category: 'boh',
    categoryLabel: 'Supply Chain',
    icon: <AssessmentIcon />,
    color: '#F97316', // Orange
    roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'],
    keywords: ['waste', 'spoilage', 'variance', 'shrinkage', 'loss', 'audit']
  },
  {
    id: 'marketplace',
    name: 'Supplier Marketplace',
    description: 'B2B wholesale marketplace, distributor pricing & quick ordering',
    path: '/marketplace',
    category: 'boh',
    categoryLabel: 'Supply Chain',
    icon: <StorefrontIcon />,
    color: '#14B8A6', // Teal
    roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'],
    keywords: ['wholesale', 'b2b', 'marketplace', 'distributor', 'procurement']
  },

  // --- Workforce & Guests ---
  {
    id: 'clients',
    name: 'Clients & Locations',
    description: 'Onboard client businesses, configure locations and manage tenant access',
    path: '/clients',
    category: 'workforce',
    categoryLabel: 'Workforce',
    icon: <ApartmentIcon />,
    color: '#0F766E',
    roles: ['ROLE_SUPER_ADMIN'],
    keywords: ['clients', 'tenants', 'onboarding', 'locations', 'stores', 'branches']
  },
  {
    id: 'employees',
    name: 'Staff & RBAC Roster',
    description: 'Team member directory, roles, pin access codes & permissions',
    path: '/employees',
    category: 'workforce',
    categoryLabel: 'Workforce',
    icon: <BadgeIcon />,
    color: '#0284C7', // Sky blue
    roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'],
    keywords: ['staff', 'team', 'employees', 'roles', 'permissions', 'rbac', 'users']
  },
  {
    id: 'scheduling',
    name: 'Shift Scheduling',
    description: 'Weekly team schedules, shift swaps, labor budgeting & coverage',
    path: '/scheduling',
    category: 'workforce',
    categoryLabel: 'Workforce',
    icon: <CalendarMonthIcon />,
    color: '#84CC16', // Lime
    roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'],
    keywords: ['calendar', 'shifts', 'schedule', 'roster', 'labor cost', 'coverage']
  },
  {
    id: 'customers',
    name: 'Customer Loyalty & CRM',
    description: 'Guest profiles, loyalty point rewards & purchase history',
    path: '/customers',
    category: 'workforce',
    categoryLabel: 'Workforce',
    icon: <PeopleIcon />,
    color: '#A855F7', // Violet
    roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'],
    keywords: ['crm', 'loyalty', 'guests', 'rewards', 'vip', 'marketing', 'profiles']
  },
  {
    id: 'booking-calendar',
    name: 'Table & Order Bookings',
    description: 'Dining reservation grid, event bookings & waitlist manager',
    path: '/booking-calendar',
    category: 'workforce',
    categoryLabel: 'Workforce',
    icon: <CalendarMonthIcon />,
    color: '#D946EF', // Fuchsia
    roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'],
    keywords: ['reservation', 'tables', 'bookings', 'calendar', 'waitlist', 'dining']
  },

  // --- Executive & Intelligence ---
  {
    id: 'dashboard',
    name: 'Merchant Dashboard',
    description: 'Executive overview, real-time store health & modular KPI widgets',
    path: '/',
    category: 'intelligence',
    categoryLabel: 'Executive',
    icon: <DashboardIcon />,
    color: '#10B981', // Emerald
    roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_CASHIER'],
    keywords: ['home', 'overview', 'dashboard', 'sales', 'today', 'metrics', 'kpi']
  },
  {
    id: 'analytics',
    name: 'Executive Analytics',
    description: 'Deep financial trends, hourly sales velocity & gross profit margins',
    path: '/analytics',
    category: 'intelligence',
    categoryLabel: 'Executive',
    icon: <AssessmentIcon />,
    color: '#2563EB', // Blue
    roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'],
    keywords: ['reports', 'analytics', 'charts', 'revenue', 'profit', 'margin', 'trends']
  },
  {
    id: 'ai-insights',
    name: 'AI Strategic Insights',
    description: 'Autonomous copilot forecasts, dynamic pricing & demand anomalies',
    path: '/ai-insights',
    category: 'intelligence',
    categoryLabel: 'Executive',
    icon: <SmartToyIcon />,
    color: '#9333EA', // Purple
    roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'],
    keywords: ['copilot', 'ai', 'forecast', 'anomalies', 'suggestions', 'smart']
  },
  {
    id: 'transactions',
    name: 'Transactions & Receipts',
    description: 'Audit log of all tender sales, refunds, voids & digital receipts',
    path: '/transactions',
    category: 'intelligence',
    categoryLabel: 'Executive',
    icon: <ReceiptIcon />,
    color: '#64748B', // Slate
    roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'],
    keywords: ['sales', 'receipts', 'refunds', 'tender', 'audit', 'history', 'charges']
  },
  {
    id: 'dynamic-chart',
    name: 'Custom Metric Charts',
    description: 'Ad-hoc BI query builder, cross-dimensional data pivot visualizations',
    path: '/dynamic-chart',
    category: 'intelligence',
    categoryLabel: 'Executive',
    icon: <AssessmentIcon />,
    color: '#0EA5E9', // Sky
    roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'],
    keywords: ['bi', 'custom', 'queries', 'charts', 'dimensions', 'pivot']
  },
  {
    id: 'menu-display',
    name: 'Digital Menu Board',
    description: 'Customer digital signage, live menu item pricing & promotional slides',
    path: '/menu-display',
    category: 'foh',
    categoryLabel: 'Floor Operations',
    icon: <TvIcon />,
    color: '#F43F5E', // Rose
    roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER'],
    keywords: ['tv', 'signage', 'menu board', 'display', 'screen']
  },
  {
    id: 'customer-display',
    name: 'Customer-Facing Display',
    description: 'Dual screen customer checkout terminal with tips & e-signature',
    path: '/customer-display',
    category: 'foh',
    categoryLabel: 'Floor Operations',
    icon: <TabletMacIcon />,
    color: '#059669', // Emerald dark
    roles: ['ROLE_SUPER_ADMIN', 'ROLE_ADMIN', 'ROLE_MANAGER', 'ROLE_CASHIER'],
    keywords: ['cfd', 'customer display', 'pole display', 'tipping', 'signature']
  }
];

const DEFAULT_PINNED = ['pos', 'kitchen-display', 'cash', 'inventory', 'timeclock', 'dashboard'];

export default function AppDrawer({ open, onClose, userRole = 'ROLE_ADMIN' }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const searchInputRef = useRef(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTab, setSelectedTab] = useState('all');
  const [pinnedApps, setPinnedApps] = useState(() => {
    try {
      const saved = localStorage.getItem('sims_pinned_apps');
      return saved ? JSON.parse(saved) : DEFAULT_PINNED;
    } catch (e) {
      return DEFAULT_PINNED;
    }
  });

  // Focus search on open
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 150);
    } else {
      setSearchQuery('');
    }
  }, [open]);

  // Global keyboard shortcuts (Ctrl+K or Cmd+K or ESC)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (open) onClose();
        else window.dispatchEvent(new CustomEvent('open-app-drawer'));
      } else if (e.key === 'Escape' && open) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // Toggle app pin / favorite
  const togglePin = (appId, e) => {
    e.stopPropagation();
    setPinnedApps((prev) => {
      const updated = prev.includes(appId)
        ? prev.filter((id) => id !== appId)
        : [...prev, appId];
      try {
        localStorage.setItem('sims_pinned_apps', JSON.stringify(updated));
      } catch (err) {}
      return updated;
    });
  };

  // Filter apps based on role, category tab, and search query
  const accessibleApps = useMemo(() => {
    return APP_CATALOG.filter((app) => app.roles.includes(userRole));
  }, [userRole]);

  const filteredApps = useMemo(() => {
    let result = accessibleApps;

    if (selectedTab === 'pinned') {
      result = result.filter((app) => pinnedApps.includes(app.id));
    } else if (selectedTab !== 'all') {
      result = result.filter((app) => app.category === selectedTab);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        (app) =>
          app.name.toLowerCase().includes(q) ||
          app.description.toLowerCase().includes(q) ||
          app.categoryLabel.toLowerCase().includes(q) ||
          (app.keywords && app.keywords.some((k) => k.toLowerCase().includes(q)))
      );
    }

    return result;
  }, [accessibleApps, selectedTab, searchQuery, pinnedApps]);

  const pinnedList = useMemo(() => {
    return accessibleApps.filter((app) => pinnedApps.includes(app.id));
  }, [accessibleApps, pinnedApps]);

  const handleAppLaunch = (path) => {
    navigate(path);
    onClose();
  };

  const isDark = theme.palette.mode === 'dark';

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 540, md: 680 },
          maxWidth: '100vw',
          bgcolor: isDark ? '#0f172a' : '#ffffff',
          backgroundImage: 'none',
          boxShadow: isDark ? '0 25px 60px rgba(0,0,0,0.85)' : '0 20px 45px rgba(0,0,0,0.15)',
          display: 'flex',
          flexDirection: 'column',
          p: 0,
          borderRight: '1px solid',
          borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
        },
      }}
    >
      {/* DRAWER TOP BAR */}
      <Box
        sx={{
          p: { xs: 2, sm: 2.5 },
          pb: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: isDark ? '#1e293b' : '#f8fafc',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: 'primary.main',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(37,99,235,0.3)',
              }}
            >
              <AppsIcon />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" fontWeight="900" sx={{ letterSpacing: -0.3 }}>
                  App Drawer
                </Typography>
                <Chip
                  label={`${accessibleApps.length} Apps`}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                />
              </Box>
              <Typography variant="caption" color="text.secondary">
                Clover & Square Quick App Launcher &bull; Press <kbd style={{ padding: '2px 5px', borderRadius: 4, background: isDark ? '#334155' : '#e2e8f0' }}>Esc</kbd> to close
              </Typography>
            </Box>
          </Box>

          <IconButton onClick={onClose} edge="end" sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* SEARCH BAR */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            bgcolor: isDark ? '#0f172a' : '#ffffff',
            borderRadius: 2.5,
            px: 2,
            py: 0.75,
            border: '1px solid',
            borderColor: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
            boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
            transition: 'border-color 0.2s ease',
            '&:focus-within': {
              borderColor: 'primary.main',
              boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}33`,
            },
          }}
        >
          <SearchIcon sx={{ color: 'text.secondary', mr: 1, fontSize: '1.2rem' }} />
          <InputBase
            inputRef={searchInputRef}
            fullWidth
            placeholder="Search all apps, actions, or tools (e.g., POS, KDS, Stock, Clock)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ fontSize: '0.9rem', fontWeight: 500 }}
          />
          {searchQuery && (
            <IconButton size="small" onClick={() => setSearchQuery('')}>
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* CATEGORY TABS */}
        <Tabs
          value={selectedTab}
          onChange={(e, val) => setSelectedTab(val)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 38,
            mt: 1.5,
            '& .MuiTab-root': {
              minHeight: 36,
              py: 0.5,
              px: 1.5,
              fontSize: '0.8rem',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 2,
              mr: 0.5,
            },
          }}
        >
          <Tab value="all" label="All Apps" />
          <Tab
            value="pinned"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <StarIcon sx={{ fontSize: '0.9rem', color: '#F59E0B' }} />
                <span>Favorites ({pinnedList.length})</span>
              </Box>
            }
          />
          <Tab value="foh" label="Floor & POS" />
          <Tab value="boh" label="Supply Chain" />
          <Tab value="workforce" label="Workforce" />
          <Tab value="intelligence" label="Analytics & AI" />
        </Tabs>
      </Box>

      {/* DRAWER BODY (SCROLLABLE APP GRID) */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: { xs: 2, sm: 2.5 } }}>
        {/* QUICK PINNED SHORTCUT STRIP (Shown when not in search or pinned tab) */}
        {!searchQuery && selectedTab === 'all' && pinnedList.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, px: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary' }}>
                ⭐ Quick Launch Favorites
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Tap star to pin/unpin
              </Typography>
            </Box>
            <Grid container spacing={1.5}>
              {pinnedList.slice(0, 6).map((app) => (
                <Grid item xs={4} sm={2} key={`quick-${app.id}`}>
                  <CardActionArea
                    onClick={() => handleAppLaunch(app.path)}
                    sx={{
                      p: 1.25,
                      borderRadius: 2.5,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                      border: '1px solid',
                      borderColor: location.pathname === app.path ? 'primary.main' : 'divider',
                      '&:hover': {
                        bgcolor: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)',
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.18s ease',
                    }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        bgcolor: `${app.color}18`,
                        color: app.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 1,
                        fontSize: '1.4rem',
                      }}
                    >
                      {app.icon}
                    </Box>
                    <Typography
                      variant="caption"
                      fontWeight="700"
                      noWrap
                      sx={{
                        width: '100%',
                        fontSize: '0.72rem',
                        color: location.pathname === app.path ? 'primary.main' : 'text.primary',
                      }}
                    >
                      {app.name.split(' (')[0]}
                    </Typography>
                  </CardActionArea>
                </Grid>
              ))}
            </Grid>
            <Divider sx={{ mt: 2.5 }} />
          </Box>
        )}

        {/* SECTION HEADER */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, px: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary' }}>
            {selectedTab === 'all'
              ? searchQuery ? `Search Results (${filteredApps.length})` : `All System Modules (${filteredApps.length})`
              : selectedTab === 'pinned'
              ? `Pinned Favorites (${filteredApps.length})`
              : `${filteredApps[0]?.categoryLabel || 'Modules'} (${filteredApps.length})`}
          </Typography>
        </Box>

        {/* NO APPS FOUND EMPTY STATE */}
        {filteredApps.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
            <AppsIcon sx={{ fontSize: 48, opacity: 0.4, mb: 1.5 }} />
            <Typography variant="body1" fontWeight="700">
              No matching applications found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Try searching with another keyword or clear your active filter.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={1.75}>
            {filteredApps.map((app) => {
              const isActive = location.pathname === app.path;
              const isPinned = pinnedApps.includes(app.id);

              return (
                <Grid item xs={12} sm={6} key={app.id}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: isActive
                        ? 'primary.main'
                        : isDark
                        ? 'rgba(255,255,255,0.08)'
                        : 'rgba(0,0,0,0.08)',
                      bgcolor: isActive
                        ? isDark
                          ? 'rgba(37,99,235,0.12)'
                          : 'rgba(37,99,235,0.05)'
                        : isDark
                        ? '#1e293b'
                        : '#ffffff',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.2s cubic-bezier(0.4,0,0.2,1)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: isDark
                          ? '0 12px 24px rgba(0,0,0,0.5)'
                          : '0 8px 20px rgba(0,0,0,0.08)',
                        borderColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                      },
                    }}
                  >
                    <Box
                      onClick={() => handleAppLaunch(app.path)}
                      sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start', cursor: 'pointer' }}
                    >
                      <Box sx={{ display: 'flex', width: '100%', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5 }}>
                        {/* ICON BADGE */}
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2.5,
                            bgcolor: `${app.color}20`,
                            color: app.color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: `0 4px 14px ${app.color}25`,
                          }}
                        >
                          {app.icon}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {isActive && (
                            <Chip
                              label="Active"
                              size="small"
                              color="primary"
                              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 800 }}
                            />
                          )}
                          <Tooltip title={isPinned ? 'Remove from favorites' : 'Pin to favorites'}>
                            <IconButton
                              size="small"
                              onClick={(e) => togglePin(app.id, e)}
                              sx={{
                                color: isPinned ? '#F59E0B' : 'text.disabled',
                                '&:hover': { color: '#F59E0B' },
                              }}
                            >
                              {isPinned ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      {/* TITLE & DESCRIPTION */}
                      <Typography
                        variant="subtitle1"
                        fontWeight="800"
                        sx={{
                          lineHeight: 1.2,
                          mb: 0.5,
                          color: isActive ? 'primary.main' : 'text.primary',
                        }}
                      >
                        {app.name}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          fontSize: '0.8rem',
                          lineHeight: 1.35,
                          mb: 1.5,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {app.description}
                      </Typography>

                      {/* FOOTER BADGES */}
                      <Box sx={{ mt: 'auto', display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Chip
                          label={app.categoryLabel}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.68rem',
                            fontWeight: 600,
                            bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                          }}
                        />
                        <Typography variant="caption" sx={{ ml: 'auto', fontWeight: 700, color: 'primary.main', fontSize: '0.75rem' }}>
                          Launch &rarr;
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>

      {/* DRAWER FOOTER (QUICK STATUS & STATS) */}
      <Box
        sx={{
          p: 2,
          px: 2.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: isDark ? '#1e293b' : '#f8fafc',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10B981' }} />
          <Typography variant="caption" fontWeight="600" color="text.secondary">
            SIMS Hospitality OS &bull; Clover & Square Parity Suite
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          Role: <strong>{userRole.replace('ROLE_', '')}</strong>
        </Typography>
      </Box>
    </Drawer>
  );
}
